import clsx from 'clsx';
import type { MLBProbablePitcher } from '../../types';

interface Props {
  awayPitcher?: MLBProbablePitcher;
  homePitcher?: MLBProbablePitcher;
  awayTeamAbbr: string;
  homeTeamAbbr: string;
}

function StatCell({ label, value, tier }: { label: string; value: string; tier?: 'good' | 'warn' | 'bad' | null }) {
  const color = tier === 'good' ? 'text-win' : tier === 'bad' ? 'text-loss' : tier === 'warn' ? 'text-warn' : 'text-gray-200';
  return (
    <div className="text-center">
      <div className={clsx('text-sm font-mono font-semibold', color)}>{value}</div>
      <div className="text-[10px] text-gray-500 uppercase tracking-wide mt-0.5">{label}</div>
    </div>
  );
}

function eraColor(era: number): 'good' | 'warn' | 'bad' {
  if (era < 3.50) return 'good';
  if (era < 4.50) return 'warn';
  return 'bad';
}

function whipColor(whip: number): 'good' | 'warn' | 'bad' {
  if (whip < 1.15) return 'good';
  if (whip < 1.35) return 'warn';
  return 'bad';
}

function PitcherPanel({ pitcher, teamAbbr }: { pitcher?: MLBProbablePitcher; teamAbbr: string }) {
  if (!pitcher) {
    return (
      <div className="card p-4 flex items-center justify-center text-gray-600 text-sm h-full">
        <span>{teamAbbr} — TBD</span>
      </div>
    );
  }

  const s = pitcher.stats;
  const restLabel = pitcher.stats?.restDays != null
    ? pitcher.stats.restDays === 0
      ? 'Today'
      : pitcher.stats.restDays === 1
        ? '1 day rest'
        : `${pitcher.stats.restDays}d rest`
    : null;

  const handLabel = pitcher.handedness === 'L' ? 'LHP' : pitcher.handedness === 'R' ? 'RHP' : '';

  return (
    <div className="card p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold text-gray-100">{pitcher.fullName}</div>
          <div className="text-xs text-gray-500 flex items-center gap-2 mt-0.5">
            {handLabel && <span className="text-brand font-mono">{handLabel}</span>}
            {restLabel && <span>{restLabel}</span>}
          </div>
        </div>
        <div className="text-xs font-mono text-gray-500 uppercase tracking-wider">{teamAbbr}</div>
      </div>

      {s ? (
        <>
          <div className="grid grid-cols-4 gap-3 pt-2 border-t border-surface-4">
            <StatCell label="ERA" value={s.era.toFixed(2)} tier={eraColor(s.era)} />
            <StatCell label="WHIP" value={s.whip.toFixed(2)} tier={whipColor(s.whip)} />
            <StatCell label="K/9" value={s.kPer9.toFixed(1)} tier={s.kPer9 >= 9 ? 'good' : s.kPer9 >= 7 ? 'warn' : 'bad'} />
            <StatCell label="BB/9" value={s.bbPer9.toFixed(1)} tier={s.bbPer9 <= 2.5 ? 'good' : s.bbPer9 <= 3.5 ? 'warn' : 'bad'} />
          </div>

          {(s.fip != null || s.last5Era != null) && (
            <div className="grid grid-cols-3 gap-3 pt-2 border-t border-surface-4">
              {s.fip != null && <StatCell label="FIP" value={s.fip.toFixed(2)} tier={eraColor(s.fip)} />}
              {s.xfip != null && <StatCell label="xFIP" value={s.xfip.toFixed(2)} tier={eraColor(s.xfip)} />}
              {s.last5Era != null && (
                <StatCell
                  label="Last 5"
                  value={s.last5Era.toFixed(2)}
                  tier={eraColor(s.last5Era)}
                />
              )}
            </div>
          )}

          {(s.whiffPct != null || s.barrelPctAgainst != null) && (
            <div className="grid grid-cols-2 gap-3 pt-2 border-t border-surface-4">
              {s.whiffPct != null && (
                <StatCell
                  label="Whiff%"
                  value={`${(s.whiffPct * 100).toFixed(1)}%`}
                  tier={s.whiffPct >= 0.27 ? 'good' : s.whiffPct >= 0.22 ? 'warn' : 'bad'}
                />
              )}
              {s.barrelPctAgainst != null && (
                <StatCell
                  label="Barrel%"
                  value={`${(s.barrelPctAgainst * 100).toFixed(1)}%`}
                  tier={s.barrelPctAgainst <= 0.065 ? 'good' : s.barrelPctAgainst <= 0.09 ? 'warn' : 'bad'}
                />
              )}
            </div>
          )}

          <div className="text-[10px] text-gray-600 pt-1">
            {s.innings.toFixed(0)} IP · {s.wins}W-{s.losses}L · {s.strikeouts}K
          </div>
        </>
      ) : (
        <div className="text-xs text-gray-600 pt-2">Stats not yet available.</div>
      )}
    </div>
  );
}

export function PitchingIntelligence({ awayPitcher, homePitcher, awayTeamAbbr, homeTeamAbbr }: Props) {
  return (
    <div className="space-y-2">
      <div className="text-xs font-medium text-gray-400 uppercase tracking-wider">Pitching Matchup</div>
      <div className="grid grid-cols-2 gap-4">
        <PitcherPanel pitcher={awayPitcher} teamAbbr={awayTeamAbbr} />
        <PitcherPanel pitcher={homePitcher} teamAbbr={homeTeamAbbr} />
      </div>
    </div>
  );
}
